import React, { useEffect, useState } from 'react';

// Code Examples Tabs Component
const CodeExampleTabs = () => {
  const [activeTab, setActiveTab] = useState('curl');

  const tabs = [
    { id: 'curl', label: 'cURL' },
    { id: 'python', label: 'Python' },
    { id: 'javascript', label: 'JavaScript' },
    { id: 'postman', label: 'Postman' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'curl':
        return (
          <div className="bg-gray-900 text-gray-100 p-6 rounded-b-lg overflow-x-auto">
            <pre><code>{`curl -X POST "https://api-asi-agents.us.inc/api/finance/v1/chat/completions" \\
  -H "Authorization: Bearer your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "asi1-finance-agent",
    "messages": [
      {
        "role": "user",
        "content": "What are the key factors to consider when investing in stocks?"
      }
    ],
    "max_tokens": 500,
    "temperature": 0.7,
    "stream": false,
    "web_search": false
  }'`}</code></pre>
          </div>
        );
      case 'python':
        return (
          <div className="bg-gray-900 text-gray-100 p-6 rounded-b-lg overflow-x-auto">
            <pre><code>{`from openai import OpenAI

# Initialize the client
client = OpenAI(
    base_url="https://api-asi-agents.us.inc/api/finance/v1",
    api_key="your-api-key"
)

# Create a chat completion
completion = client.chat.completions.create(
    model="asi1-finance-agent",
    messages=[
        {
            "role": "user",
            "content": "Explain the concept of compound interest in investing"
        }
    ],
    max_tokens=500,
    temperature=0.7,
    stream=False,
    extra_body={
        "web_search": False
    }
)

print(completion.choices[0].message.content)`}</code></pre>
          </div>
        );
      case 'javascript':
        return (
          <div className="bg-gray-900 text-gray-100 p-6 rounded-b-lg overflow-x-auto">
            <pre><code>{`const response = await fetch('https://api-asi-agents.us.inc/api/finance/v1/chat/completions', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your-api-key',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    model: 'asi1-finance-agent',
    messages: [
      {
        role: 'user',
        content: 'What are the current trends in cryptocurrency markets?'
      }
    ],
    max_tokens: 500,
    temperature: 0.7,
    stream: false,
    web_search: true
  })
});

const data = await response.json();
console.log(data.choices[0].message.content);`}</code></pre>
          </div>
        );
      case 'postman':
        return (
          <div className="bg-gray-50 p-6 rounded-b-lg">
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">1. Request Configuration</h4>
                <div className="bg-white p-4 rounded border">
                  <p className="mb-2"><strong>Method:</strong> POST</p>
                  <p><strong>URL:</strong> <code className="bg-gray-100 px-2 py-1 rounded">https://api-asi-agents.us.inc/api/finance/v1/chat/completions</code></p>
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">2. Headers</h4>
                <div className="bg-white p-4 rounded border">
                  <p className="mb-2"><code>Content-Type: application/json</code></p>
                  <p><code>Authorization: Bearer your-api-key</code></p>
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">3. Body (raw JSON)</h4>
                <div className="bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto">
                  <pre><code>{`{
  "model": "asi1-finance-agent",
  "messages": [
    {
      "role": "user",
      "content": "Help me create a diversified investment portfolio"
    }
  ],
  "max_tokens": 800,
  "temperature": 0.7,
  "stream": false,
  "web_search": true
}`}</code></pre>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Tab Headers */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-green-600 bg-white border-b-2 border-green-600'
                : 'text-gray-600 hover:text-green-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

const ASI1FinanceExpertAgent = () => {
  // Ensure scroll position is at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="container mx-auto sm:px-6 md:px-4 sm:py-8 max-w-7xl" style={{ paddingLeft: '0.1rem', paddingRight: '0.1rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header section */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 px-8 py-12 text-white">
          <h1 className="text-4xl font-bold mb-4">ASI Finance Expert Agent</h1>
          <p className="text-xl opacity-90">
            Specialized AI assistant powered by the proprietary Multiple Expert Router Model (MERM) architecture with 16+ trillion tokens training.
          </p>
        </div>

        {/* Documentation content */}
        <div className="px-6 md:px-12 lg:px-16 py-8">
          {/* Key Features */}
          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-green-50 p-6 rounded-lg border border-green-100">
                <h3 className="text-lg font-semibold text-green-700 mb-2">Finance-Only Expertise</h3>
                <p className="text-gray-700">
                  Exclusively handles financial queries including investments, market analysis, financial planning, and economic insights.
                </p>
              </div>
              <div className="bg-green-50 p-6 rounded-lg border border-green-100">
                <h3 className="text-lg font-semibold text-green-700 mb-2">Advanced Features</h3>
                <p className="text-gray-700">
                  Real-time streaming, web search integration, and OpenAI-compatible API for seamless integration.
                </p>
              </div>
              <div className="bg-green-50 p-6 rounded-lg border border-green-100">
                <h3 className="text-lg font-semibold text-green-700 mb-2">MERM Architecture</h3>
                <p className="text-gray-700">
                  Powered by Multiple Expert Router Model with 16+ trillion tokens training for superior financial expertise.
                </p>
              </div>
            </div>
          </section>

          {/* Getting Started */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Getting Started</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 font-bold text-lg">1</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Get Your API Key</h3>
                <p className="text-gray-600">Contact the Fetch AI team to obtain your API key for accessing the ASI Expert Finance Agent.</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 font-bold text-lg">2</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Make Your First Request</h3>
                <p className="text-gray-600">Send a POST request to our finance endpoint with your financial question.</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 font-bold text-lg">3</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Integrate & Scale</h3>
                <p className="text-gray-600">Use our OpenAI-compatible API to integrate with your existing applications.</p>
              </div>
            </div>
          </section>

          {/* Authentication */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Authentication</h2>
            <p className="text-gray-700 mb-6">
              The ASI Expert Agents API uses API key authentication. You can provide your API key in two ways:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Bearer Token (Recommended)</h3>
                <code className="block bg-gray-800 text-green-400 p-3 rounded text-sm">
                  Authorization: Bearer your-api-key-here
                </code>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Custom Header</h3>
                <code className="block bg-gray-800 text-green-400 p-3 rounded text-sm">
                  X-API-Key: your-api-key-here
                </code>
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mt-4">
              <p className="text-yellow-800 font-medium">
                <strong>Security Note:</strong> Always keep your API key secure and never expose it in client-side code.
              </p>
            </div>
          </section>

          {/* API Reference */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">API Reference</h2>
            
            {/* Chat Completions Endpoint */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <span className="bg-green-600 text-white px-3 py-1 rounded text-sm font-medium mr-3">POST</span>
                <code className="text-lg font-mono">/api/finance/v1/chat/completions</code>
              </div>
              <p className="text-gray-700 mb-4">Create a chat completion with the ASI Expert Finance Agent.</p>
              
              <h4 className="text-lg font-semibold mb-3">Request Parameters</h4>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 border-b">Parameter</th>
                      <th className="text-left p-3 border-b">Type</th>
                      <th className="text-left p-3 border-b">Required</th>
                      <th className="text-left p-3 border-b">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 border-b font-mono">model</td>
                      <td className="p-3 border-b">string</td>
                      <td className="p-3 border-b text-red-600">required</td>
                      <td className="p-3 border-b">Must be "asi1-finance-agent"</td>
                    </tr>
                    <tr>
                      <td className="p-3 border-b font-mono">messages</td>
                      <td className="p-3 border-b">array</td>
                      <td className="p-3 border-b text-red-600">required</td>
                      <td className="p-3 border-b">Array of conversation messages</td>
                    </tr>
                    <tr>
                      <td className="p-3 border-b font-mono">max_tokens</td>
                      <td className="p-3 border-b">integer</td>
                      <td className="p-3 border-b text-gray-500">optional</td>
                      <td className="p-3 border-b">Maximum number of tokens to generate</td>
                    </tr>
                    <tr>
                      <td className="p-3 border-b font-mono">temperature</td>
                      <td className="p-3 border-b">float</td>
                      <td className="p-3 border-b text-gray-500">optional</td>
                      <td className="p-3 border-b">Controls randomness (0-2)</td>
                    </tr>
                    <tr>
                      <td className="p-3 border-b font-mono">stream</td>
                      <td className="p-3 border-b">boolean</td>
                      <td className="p-3 border-b text-gray-500">optional</td>
                      <td className="p-3 border-b">Enable streaming responses (default: false)</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono">web_search</td>
                      <td className="p-3">boolean</td>
                      <td className="p-3 text-gray-500">optional</td>
                      <td className="p-3">Enable web search capabilities (default: false)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Other Endpoints */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium mr-3">GET</span>
                  <code className="font-mono">/api/finance/v1/models</code>
                </div>
                <p className="text-gray-700">List available finance expert models.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium mr-3">GET</span>
                  <code className="font-mono">/api/finance/v1/health</code>
                </div>
                <p className="text-gray-700">Check the health status of the Finance Expert Agent API.</p>
              </div>
            </div>
          </section>

          {/* Use Cases */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Use Cases</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Investment Analysis</h3>
                <p className="text-gray-700 mb-3">Get expert analysis on investment opportunities, portfolio optimization, and risk assessment.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Stock analysis and recommendations</li>
                  <li>• Portfolio diversification strategies</li>
                  <li>• Risk vs. return analysis</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Financial Planning</h3>
                <p className="text-gray-700 mb-3">Comprehensive financial planning assistance for individuals and businesses.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Retirement planning calculations</li>
                  <li>• Budget optimization</li>
                  <li>• Tax planning strategies</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Market Research</h3>
                <p className="text-gray-700 mb-3">Real-time market insights and economic analysis with web search capabilities.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Market trend analysis</li>
                  <li>• Economic indicator interpretation</li>
                  <li>• Sector-specific insights</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Cryptocurrency</h3>
                <p className="text-gray-700 mb-3">Expert guidance on cryptocurrency investments and blockchain technology.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Crypto portfolio analysis</li>
                  <li>• DeFi strategy recommendations</li>
                  <li>• Blockchain technology insights</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Risk Management & Insurance</h3>
                <p className="text-gray-700 mb-3">Comprehensive risk assessment and insurance planning to protect your financial assets.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Insurance needs analysis</li>
                  <li>• Risk assessment and mitigation strategies</li>
                  <li>• Hedging strategies and derivatives</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Corporate Finance & Valuation</h3>
                <p className="text-gray-700 mb-3">Expert business valuation and corporate financial strategy for enterprises and investors.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Business valuation models</li>
                  <li>• Capital structure optimization</li>
                  <li>• M&A financial analysis</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Real Estate Investment</h3>
                <p className="text-gray-700 mb-3">Strategic real estate investment analysis and market evaluation for property investors.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Property investment analysis</li>
                  <li>• REITs evaluation</li>
                  <li>• Real estate market trends</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">International Finance & Forex</h3>
                <p className="text-gray-700 mb-3">Global financial strategies and currency exchange guidance for international investments.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Currency exchange strategies</li>
                  <li>• International investment opportunities</li>
                  <li>• Cross-border financial planning</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Personal Credit & Debt Management</h3>
                <p className="text-gray-700 mb-3">Optimize personal credit profiles and develop effective debt management strategies.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Credit score optimization</li>
                  <li>• Debt consolidation strategies</li>
                  <li>• Loan and mortgage analysis</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">ESG & Sustainable Finance</h3>
                <p className="text-gray-700 mb-3">Environmental, social, and governance investment strategies for responsible investing.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• ESG investment screening</li>
                  <li>• Impact investing strategies</li>
                  <li>• Sustainability reporting</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Wealth Management & Private Banking</h3>
                <p className="text-gray-700 mb-3">Exclusive financial services for high-net-worth individuals and families.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Private wealth advisory services</li>
                  <li>• Estate planning and succession</li>
                  <li>• Family office management strategies</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-green-700 mb-3">Financial Technology & Innovation</h3>
                <p className="text-gray-700 mb-3">Cutting-edge fintech trends and digital financial solutions for modern investing.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Fintech trends and analysis</li>
                  <li>• Digital payment solutions</li>
                  <li>• Robo-advisory platform insights</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Code Examples */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Code Examples</h2>
            <CodeExampleTabs />
          </section>

          {/* Error Handling */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error Handling</h2>
            <div className="space-y-6">
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <div className="bg-red-50 px-4 py-2">
                  <span className="font-semibold text-red-800">401 Unauthorized</span>
                  <span className="ml-4 text-red-700">Invalid or missing API key.</span>
                </div>
                <div className="bg-gray-900 text-gray-100 p-4">
                  <pre><code>{`{
  "error": {
    "message": "Invalid API key",
    "type": "authentication_error",
    "code": 401
  }
}`}</code></pre>
                </div>
              </div>

              <div className="border border-orange-200 rounded-lg overflow-hidden">
                <div className="bg-orange-50 px-4 py-2">
                  <span className="font-semibold text-orange-800">400 Bad Request</span>
                  <span className="ml-4 text-orange-700">Invalid model or request format.</span>
                </div>
                <div className="bg-gray-900 text-gray-100 p-4">
                  <pre><code>{`{
  "error": {
    "message": "Invalid model 'gpt-4'. Only 'asi1-finance-agent' is supported for finance queries.",
    "type": "invalid_request_error",
    "param": "model",
    "code": "model_not_found"
  }
}`}</code></pre>
                </div>
              </div>

              <div className="border border-yellow-200 rounded-lg overflow-hidden">
                <div className="bg-yellow-50 px-4 py-2">
                  <span className="font-semibold text-yellow-800">403 Forbidden</span>
                  <span className="ml-4 text-yellow-700">Security violation or non-finance query detected.</span>
                </div>
                <div className="bg-gray-900 text-gray-100 p-4">
                  <pre><code>{`{
  "error": {
    "message": "I am a specialized finance expert and can only assist with finance-related queries.",
    "type": "security_violation",
    "param": "messages",
    "code": "unauthorized_access_attempt"
  }
}`}</code></pre>
                </div>
              </div>

              <div className="border border-red-300 rounded-lg overflow-hidden">
                <div className="bg-red-100 px-4 py-2">
                  <span className="font-semibold text-red-800">502 Server Error</span>
                  <span className="ml-4 text-red-700">Technical difficulties with the service.</span>
                </div>
                <div className="bg-gray-900 text-gray-100 p-4">
                  <pre><code>{`{
  "error": {
    "message": "We're experiencing technical difficulties. Our team has been notified and is working to resolve this issue.",
    "type": "server_error",
    "code": 502
  }
}`}</code></pre>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ASI1FinanceExpertAgent;
